import { Injectable } from '@angular/core';
import { LocalService } from './local.service';
import { environment } from '../../../environments/environment';

/** What the callback page sends to the Personalia backend to finish the login. */
export interface SokratesOidcCallbackResult {
  code: string;
  codeVerifier: string;
  nonce: string;
  redirectUri: string;
}

interface PendingOidcLogin {
  state: string;
  nonce: string;
  codeVerifier: string;
  redirectUri: string;
}

/**
 * "Login with Sokrates" over OpenID Connect (authorization code + PKCE), the same flow as Event.
 * The browser goes to the SSO's /oidc/authorize and comes back to /auth/sso/callback with a code;
 * the Personalia backend exchanges it (with the client secret) and returns the Personalia session.
 */
@Injectable({
  providedIn: 'root'
})
export class SokratesSsoService {

  private readonly redirectStorageKey = 'personalia.sso.redirect';
  private readonly pendingLoginKey = 'personalia.sso.oidc';
  private readonly idTokenKey = 'personalia.sso.id_token';
  private readonly redirectPath = '/auth/sso/callback';
  private readonly logoutRedirectPath = '/auth';

  constructor(
    private localService: LocalService,
  ) { }

  /** OIDC issuer of the Sokrates SSO, e.g. https://api.sokrates.co.id/sso-new-http */
  get issuer(): string {
    return `${this.localService.getData('api.url')}/sso-new-http`;
  }

  get clientId(): string {
    return environment.sso.product_code;
  }

  /** Personalia signs in through SSO only (environment.sso.force): /auth goes straight to SSO. */
  isForced(): boolean {
    return environment.sso.force;
  }

  async loginWithRedirect(tenantUuid: string | null): Promise<void> {
    const pending: PendingOidcLogin = {
      state: randomString(),
      nonce: randomString(),
      codeVerifier: randomString(),
      redirectUri: window.location.origin + this.redirectPath,
    };
    sessionStorage.setItem(this.pendingLoginKey, JSON.stringify(pending));

    const url = new URL(`${this.issuer}/oidc/authorize`);
    url.searchParams.set('client_id', this.clientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', pending.redirectUri);
    // `tenant` releases tenant_uuid and roles: Personalia signs in to the school picked on this site.
    url.searchParams.set('scope', 'openid profile email tenant');
    url.searchParams.set('state', pending.state);
    url.searchParams.set('nonce', pending.nonce);
    url.searchParams.set('code_challenge', await pkceChallenge(pending.codeVerifier));
    url.searchParams.set('code_challenge_method', 'S256');
    if (tenantUuid) {
      url.searchParams.set('tenant_uuid', tenantUuid);
    }

    window.location.assign(url.toString());
  }

  /** Reads the SSO's answer on the callback page; throws when it is an error or not ours. */
  handleRedirectCallback(): SokratesOidcCallbackResult {
    const params = new URLSearchParams(window.location.search);
    const pending = this.takePendingLogin();

    if (params.get('error')) {
      throw new Error(params.get('error') === 'access_denied'
        ? 'SSO login was cancelled.'
        : params.get('error_description') || 'SSO login failed.');
    }
    if (!pending || !params.get('state') || params.get('state') !== pending.state) {
      throw new Error('SSO login session is invalid or expired. Please try again.');
    }
    if (params.get('iss') && params.get('iss') !== this.issuer) {
      throw new Error('SSO response came from an unexpected issuer.');
    }
    if (!params.get('code')) {
      throw new Error('SSO response has no authorization code.');
    }

    return {
      code: params.get('code'),
      codeVerifier: pending.codeVerifier,
      nonce: pending.nonce,
      redirectUri: pending.redirectUri,
    };
  }

  /** Kept for logout: the SSO only ends a session named by an id_token it issued. */
  setIdToken(idToken: string | null): void {
    if (idToken) {
      localStorage.setItem(this.idTokenKey, idToken);
    } else {
      localStorage.removeItem(this.idTokenKey);
    }
  }

  getIdToken(): string | null {
    return localStorage.getItem(this.idTokenKey);
  }

  /** SSO logout URL that comes back to the Personalia login page, or null without an SSO login. */
  endSessionUrl(idToken: string | null): string | null {
    if (!idToken) {
      return null;
    }
    const url = new URL(`${this.issuer}/oidc/logout`);
    url.searchParams.set('id_token_hint', idToken);
    url.searchParams.set('post_logout_redirect_uri', window.location.origin + this.logoutRedirectPath);
    return url.toString();
  }

  setRedirectTarget(redirectTo: string | null): void {
    if (!this.isSafeInternalRedirect(redirectTo)) {
      return;
    }

    sessionStorage.setItem(this.redirectStorageKey, redirectTo);
  }

  consumeRedirectTarget(): string | null {
    const redirectTo = sessionStorage.getItem(this.redirectStorageKey);
    sessionStorage.removeItem(this.redirectStorageKey);

    return this.isSafeInternalRedirect(redirectTo) ? redirectTo : null;
  }

  private takePendingLogin(): PendingOidcLogin | null {
    const raw = sessionStorage.getItem(this.pendingLoginKey);
    sessionStorage.removeItem(this.pendingLoginKey);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private isSafeInternalRedirect(redirectTo: string | null): redirectTo is string {
    return !!redirectTo
      && redirectTo.startsWith('/')
      && !redirectTo.startsWith('//')
      && !/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(redirectTo);
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(byte => binary += String.fromCharCode(byte));
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}
